import './shared/assets/font/orkney/orkney.scss';
import ReactDOM from 'react-dom/client';
import 'antd/dist/reset.css';
import './shared/assets/styles/app.scss';
import './index.scss';
import esES from 'antd/locale/es_ES';
import './i18n';
import { ConfigProvider } from 'antd';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { AuthProvider } from 'react-oidc-context';
import { routes } from './config/routes';
import { configAxios } from './shared/configs/axios-config';
import { ensureFreshSession, getAuthProviderProps, getOidcConfig, watchSessionChanges } from './shared/configs/auth-config';
import { configAnt, darkVariant, themeConfig } from './shared/configs/ant-theme-config';
import config from './config/app-config';
import { ConfigProviderProps } from 'antd/es/config-provider';
import { HealthApiService } from './api/health-api';
import ErrorPage from './error-page';
import i18n from './i18n';
import CookiebotProvider from 'src/shared/components/cookiebot-provider';
import UserAuthGuard from './user-auth-guard';
import SessionGuard from 'src/shared/components/session-guard';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import Analytics from './analytics';
// import reportWebVitals from './reportWebVitals';

console.log(`${import.meta.env.VITE_APP_NAME} ${import.meta.env.VITE_APP_VERSION}`);

/**
 * Una sola forma de decir «esto es produccion» en el arbol de React. El arranque del `<head>` tiene
 * que repetir la condicion por su cuenta -- es JavaScript suelto en el HTML y no puede importar nada
 * de aqui --, y ademas exige clave de Cookiebot, que es lo que hace que sin CMP no se cargue nada.
 * Estas dos son las que si pueden compartirla.
 */
const isProduction = window.REACT_APP_ENV === 'prod';

// configOidcLogging();
configAnt();
configAxios({
  baseUrl: config.baseUrl,
  useBearerToken: {
    authority: config.authority,
    clientId: config.clientId
  },
  onHttp401Error:
    process.env.NODE_ENV !== 'development'
      ? undefined
      : (data) => {
          console.error('HTTP 401', { data });
        }
});
const oidcConfig = getOidcConfig({
  authority: config.authority,
  clientId: config.clientId,
  clientSecret: config.clientSecret,
  postLogoutRedirectUri: config.postLogoutRedirectUri,
  redirectUri: config.redirectUri
});

export const appConfigProviderProps: ConfigProviderProps = {
  // El boton de limpiar de los campos, legible y alcanzable. El detalle de las medidas esta en
  // `index.scss`, junto a las reglas; aqui va solo el enganche.
  //
  // Se hace por `classNames.clear`, el slot semantico que antd 6 expone para ese nodo en los dos
  // componentes, y no con un selector `.ant-*`, que es acoplarse al DOM interno de la libreria. No es
  // una regla absoluta de esta aplicacion -quedan reglas `.ant-*` en `index.scss` para cosas que el
  // slot no sabe expresar, como el margen del mensaje de error, que va acotado a `-with-help`-, pero
  // aqui no hace falta.
  //
  // Y por `classNames` y no por `styles`, que seria un estilo en linea: le ganaria tambien a los
  // estados de antd y dejaria el icono sin `:active` ni contorno de foco.
  select: { classNames: { clear: 'j2l-select-clear' } },
  input: { classNames: { clear: 'j2l-input-clear' } },
  locale: esES,
  // antd 6 monta el checkbox como `inline-flex` con `align-items: baseline` y le pone
  // `align-self: center` a la caja, asi que en una etiqueta de varias lineas -- las de condiciones de
  // uso, que ocupan tres -- la caja se va al centro vertical del bloque entero en vez de acompanar a
  // la primera linea. `flex-start` la sube, y los 2px la centran opticamente sobre esa primera linea:
  // (22px de line-height - 18px de caja) / 2. Con etiquetas de una sola linea el resultado es el mismo
  // que antes, porque ahi centro y arriba coinciden.
  //
  // Se hace por `styles.icon`, el slot semantico que antd 6 expone para la caja, y no con un selector
  // `.ant-*`: ese acoplamiento al DOM interno de la libreria es justo lo que rompio esta pantalla.
  checkbox: {
    styles: {
      icon: { alignSelf: 'flex-start', marginTop: 2 }
    }
  },
  theme: {
    ...themeConfig,
    components: {
      // antd 6 eliminó el span interno que pintaba la caja (era `.ant-checkbox-inner`, hoy el slot
      // semántico `icon`), así que la regla SCSS que la agrandaba a 18px dejó de aplicar en silencio y
      // los tres checkboxes de la app volvieron a los 16px por defecto. El tamaño sale del alias
      // `controlInteractiveSize`. Ojo con el modelo mental: antd no lo hace privado del componente,
      // lo emite como la variable global `--ant-control-interactive-size` redeclarada sobre el
      // `<label class="ant-checkbox-wrapper">`, así que hereda a todo su subárbol. Aquí da igual --
      // Table y Tree, que también la consumen, están fuera de esas etiquetas y no hay ninguno en la
      // app -- pero un componente antd anidado dentro de la etiqueta sí lo heredaría. Y antd escala
      // la marca de verificación a partir de él, que es lo que el `::after { left: 4px }` del SCSS
      // venía corrigiendo a mano.
      Checkbox: {
        controlInteractiveSize: 18,
        paddingXS: 10,
        borderRadiusSM: 2
      },
      // antd 6 deriva el borde del Input de activeBorderColor (foco) y hoverBorderColor (hover), no de
      // los colorPrimary* que se copiaron aqui del darkVariant. En antd 5.4 ambos bordes salian de
      // inputBorderHoverColor, que era colorPrimaryHover -- asi que #2b2c2e es lo que se pintaba de
      // verdad en v1, y colorPrimaryActive/#000000 nunca llego a aplicarse. Sin esto el foco heredaba
      // el colorPrimary global (#22817a) y los inputs pasaban de gris casi negro a verde azulado.
      Input: {
        borderRadiusLG: 4,
        hoverBorderColor: '#2b2c2e',
        activeBorderColor: '#2b2c2e'
      },
      DatePicker: {
        ...darkVariant,
        paddingXXS: 4
      },
      // Mismo caso que Input, del que hereda los estilos de borde.
      InputNumber: {
        hoverBorderColor: '#2b2c2e',
        activeBorderColor: '#2b2c2e'
      }
    }
  }
};

const rootNode = document.getElementById('root') as HTMLElement;

const healthApiService = new HealthApiService();

healthApiService
  .waitForHealthyBackendServices()
  .then(async (_) => {
    // Antes de montar nada: esta aplicacion tiene su propio cliente OIDC y su propia copia del
    // usuario en localStorage, y se queda igual de rancia que las otras dos cuando alguien cierra
    // sesion desde cualquiera de ellas.
    await ensureFreshSession(oidcConfig);
    watchSessionChanges(oidcConfig);

    // `getAuthProviderProps` construye el `UserManager` y se lo pasa a `AuthProvider` en vez de
    // dejar que lo construya el. Es lo que permite a `session-guard` renovar por el manager real y no
    // por `auth.signinSilent`, cuyo envoltorio despacha `activeNavigator` y dispara el velo a
    // pantalla completa de los layouts en cada renovacion de fondo. Va aqui, despues de
    // `ensureFreshSession`, porque el constructor del manager ya arranca la renovacion silenciosa.
    const authProviderProps = getAuthProviderProps(oidcConfig);

    const router = createBrowserRouter(routes, { basename: '/identity' });
    const root = ReactDOM.createRoot(rootNode);
    root.render(
      // https://stackoverflow.com/a/62237101/11567125
      // <React.StrictMode>
      <HelmetProvider>
        <Helmet>
          <title>jobs2live</title>
          <meta name="description" content="Jobs2live - La plataforma para encontrar talento tech en minutos gracias a la IA." />
        </Helmet>
        <AuthProvider {...authProviderProps}>
          <ConfigProvider {...appConfigProviderProps}>
            {/*
              `<SessionGuard>` mantiene viva la renovacion del token y avisa cuando ya no se puede
              renovar. Va DENTRO de `<ConfigProvider>` porque su modal es declarativo, asi que hereda
              de aqui el tema y el idioma; colgado mas arriba saldria con los colores por defecto de
              antd.
            */}
            <SessionGuard>
              {/*
              `<Analytics>` va DENTRO de `<ConfigProvider>` a proposito: no envuelve nada que necesite,
              solo pinta `{children}` y efectos, y desde aqui su modal de confirmacion hereda el tema en
              vez de salir con los colores por defecto de antd.
            */}
              <Analytics active={isProduction}>
                {/*
                El script de Cookiebot ya lo carga `index.html`, en el <head> y antes que nada, que
                es lo unico que permite al bloqueo automatico llegar a tiempo. El provider lo
                detecta por el id `Cookiebot` y no vuelve a inyectarlo.

                Hoy es un passthrough: nadie consume `useCookiebot()` en esta aplicacion y el flujo de
                rechazo esta apagado. Se queda porque su inyeccion de respaldo no cuesta nada y porque
                el dia que alguien use el hook ya esta montado; `blockingMode` va puesto para que esa
                inyeccion de respaldo, si alguna vez es la que actua, no vuelva al modo manual que no
                bloquea nada.
              */}
                <CookiebotProvider cbid={window.REACT_APP_COOKIEBOT_KEY} useRejectNeededCookiesFlow={false} active={isProduction} blockingMode="auto">
                  <UserAuthGuard>
                    <RouterProvider router={router} />
                  </UserAuthGuard>
                </CookiebotProvider>
              </Analytics>
            </SessionGuard>
          </ConfigProvider>
        </AuthProvider>
      </HelmetProvider>
      // </React.StrictMode>
    );

    // If you want to start measuring performance in your app, pass a function
    // to log results (for example: reportWebVitals(console.log))
    // or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
    // reportWebVitals();
  })
  .catch((_) => {
    const root = ReactDOM.createRoot(rootNode);

    root.render(
      // https://stackoverflow.com/a/62237101/11567125
      // <React.StrictMode>
      <HelmetProvider>
        <Helmet>
          <title>jobs2live</title>
          <meta name="description" content="Jobs2live - La plataforma para encontrar talento tech en minutos gracias a la IA." />
        </Helmet>
        <ConfigProvider {...appConfigProviderProps}>
          <Analytics active={window.REACT_APP_ENV === 'prod'}>
            <RouterProvider
              router={createBrowserRouter(
                [
                  {
                    path: '*',
                    element: (
                      <ErrorPage
                        message={i18n.t('La app no pudo iniciar')}
                        redirectTo={{ label: i18n.t('Recargar'), onClick: () => window.location.reload() }}
                      />
                    ),
                    errorElement: <ErrorPage />
                  }
                ],
                { basename: '/identity' }
              )}
            />
          </Analytics>
        </ConfigProvider>
      </HelmetProvider>
      // </React.StrictMode>
    );
  });
